import { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToJobSeekerApplications } from '../services/applicationsService';
import { subscribeToJobRecommendations } from '../services/jobRecommendationsService';
import { subscribeToSavedJobs, subscribeToJobAlerts } from '../services/savedJobsService';
import { subscribeToDashboardUpdates, subscribeToProfileViews } from '../services/dashboardAnalyticsService';
import { notificationsService } from '../services/notificationsService';

/**
 * Custom hook for managing real-time Supabase subscriptions
 * Enables live updates across applications, recommendations, saved jobs, and dashboard data
 * 
 * @param {Object} options - Subscription options
 * @param {boolean} options.applications - Subscribe to application updates
 * @param {boolean} options.recommendations - Subscribe to job recommendation updates
 * @param {boolean} options.savedJobs - Subscribe to saved jobs updates
 * @param {boolean} options.notifications - Subscribe to notification updates
 * @param {boolean} options.dashboard - Subscribe to dashboard analytics updates
 * @param {boolean} options.profileViews - Subscribe to profile view updates
 * @param {function} options.onApplicationUpdate - Callback for application updates
 * @param {function} options.onRecommendationUpdate - Callback for recommendation updates
 * @param {function} options.onSavedJobUpdate - Callback for saved job updates
 * @param {function} options.onNotificationUpdate - Callback for notification updates
 * @param {function} options.onDashboardUpdate - Callback for dashboard updates
 * @param {function} options.onProfileViewUpdate - Callback for profile view updates
 * @returns {Object} Subscription status and control methods
 */
export const useRealtimeSubscriptions = (options = {}) => {
  const { user } = useAuth();
  const subscriptionsRef = useRef([]);
  const isSubscribedRef = useRef(false);

  const {
    applications = false,
    recommendations = false,
    savedJobs = false,
    notifications = false,
    dashboard = false,
    profileViews = false,
    onApplicationUpdate,
    onRecommendationUpdate,
    onSavedJobUpdate,
    onNotificationUpdate,
    onDashboardUpdate,
    onProfileViewUpdate
  } = options;

  // Cleanup function
  const cleanup = useCallback(() => {
    subscriptionsRef?.current?.forEach(unsubscribe => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    });
    subscriptionsRef.current = [];
    isSubscribedRef.current = false;
  }, []);

  // Subscribe to all enabled subscriptions
  const subscribe = useCallback(() => {
    if (!user?.id || isSubscribedRef?.current) return;

    // Applications subscription
    if (applications && onApplicationUpdate) {
      const unsubscribe = subscribeToJobSeekerApplications(
        user?.id,
        onApplicationUpdate
      );
      subscriptionsRef?.current?.push(unsubscribe);
    }

    // Job recommendations subscription
    if (recommendations && onRecommendationUpdate) {
      const unsubscribe = subscribeToJobRecommendations(
        user?.id,
        onRecommendationUpdate
      );
      subscriptionsRef?.current?.push(unsubscribe);
    }

    // Saved jobs subscription
    if (savedJobs && onSavedJobUpdate) {
      const unsubscribeSavedJobs = subscribeToSavedJobs(
        user?.id,
        onSavedJobUpdate
      );
      subscriptionsRef?.current?.push(unsubscribeSavedJobs);

      const unsubscribeAlerts = subscribeToJobAlerts(
        user?.id,
        (alert) => {
          onSavedJobUpdate({ eventType: 'ALERT', alert });
        }
      );
      subscriptionsRef?.current?.push(unsubscribeAlerts);
    }

    // Notifications subscription
    if (notifications && onNotificationUpdate) {
      const unsubscribe = notificationsService?.subscribeToNotifications(
        user?.id,
        onNotificationUpdate
      );
      subscriptionsRef?.current?.push(unsubscribe);
    }

    // Dashboard updates subscription
    if (dashboard && onDashboardUpdate) {
      const unsubscribe = subscribeToDashboardUpdates(
        user?.id,
        onDashboardUpdate
      );
      subscriptionsRef?.current?.push(unsubscribe);
    }

    // Profile views subscription
    if (profileViews && onProfileViewUpdate) {
      const unsubscribe = subscribeToProfileViews(
        user?.id,
        onProfileViewUpdate
      );
      subscriptionsRef?.current?.push(unsubscribe);
    }

    isSubscribedRef.current = true;
  }, [
    user?.id,
    applications,
    recommendations,
    savedJobs,
    notifications,
    dashboard,
    profileViews,
    onApplicationUpdate,
    onRecommendationUpdate,
    onSavedJobUpdate,
    onNotificationUpdate,
    onDashboardUpdate,
    onProfileViewUpdate
  ]);

  // Setup and cleanup subscriptions
  useEffect(() => {
    if (user?.id) {
      subscribe();
    }

    return () => {
      cleanup();
    };
  }, [user?.id, subscribe, cleanup]);

  // Manual subscription control
  const resubscribe = useCallback(() => {
    cleanup();
    subscribe();
  }, [cleanup, subscribe]);

  return {
    isSubscribed: isSubscribedRef?.current,
    resubscribe,
    cleanup
  };
};

/**
 * Hook for subscribing to specific application updates
 * @param {string} applicationId - Application ID to monitor
 * @param {function} onUpdate - Callback for updates
 */
export const useApplicationSubscription = (applicationId, onUpdate) => {
  const subscriptionRef = useRef(null);

  useEffect(() => {
    if (!applicationId || !onUpdate) return;

    // Subscribe to application status history
    const { subscribeToApplicationStatusHistory } = require('../services/applicationsService');
    
    subscriptionRef.current = subscribeToApplicationStatusHistory(
      applicationId,
      onUpdate
    );

    return () => {
      if (subscriptionRef?.current) {
        subscriptionRef?.current();
      }
    };
  }, [applicationId, onUpdate]);
};

/**
 * Hook for subscribing to notification updates with unread count
 * @param {function} onUpdate - Callback for notification updates
 * @returns {Object} Notification subscription state
 */
export const useNotificationSubscription = (onUpdate) => {
  const { user } = useAuth();
  const subscriptionRef = useRef(null);

  const subscribe = useCallback(() => {
    if (!user?.id || subscriptionRef?.current) return;

    subscriptionRef.current = notificationsService?.subscribeToNotifications(
      user?.id,
      (data) => {
        onUpdate?.(data);
      }
    );
  }, [user?.id, onUpdate]);

  useEffect(() => {
    subscribe();

    return () => {
      if (subscriptionRef?.current) {
        subscriptionRef?.current();
        subscriptionRef.current = null;
      }
    };
  }, [subscribe]);

  return {
    isSubscribed: !!subscriptionRef?.current
  };
};

export default useRealtimeSubscriptions;